import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import type { UserRole } from "../auth/auth";
import type { SavedCalculation } from "./calculationTypes";
import type { CreateCalculationRequest } from "./calculationsApi";
import { createCalculation } from "./calculationsApi";

export const useCreateCalculation = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const createCalculationMutation = useMutation<
    SavedCalculation,
    unknown,
    { calculation: CreateCalculationRequest; role: UserRole }
  >({
    mutationFn: ({ calculation }) => createCalculation(calculation),
    onSuccess: async (data, { role }) => {
      const isCustomerOnDemandCalculation = role === "CUSTOMER" && data.versions.at(-1)?.price === "ON_DEMAND";
      const additionalInfo = isCustomerOnDemandCalculation
        ? " Twoje zapytanie zostało wysłane do dostawcy. Link do uzupełnionej wyceny otrzymasz droga mailową."
        : "";
      enqueueSnackbar(`Kalkulacja ${data.versions[0].name} została utworzona.${additionalInfo}`, {
        variant: "success",
      });
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === "calculations" && typeof q.queryKey[1] === "object",
      });
      queryClient.setQueryData(["calculations", data.id], data);
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się utworzyć kalkulacji. Powód: ${(error as Error).message}`, {
        variant: "error",
      });
    },
  });

  return {
    onCreate: (calculation: CreateCalculationRequest, role: UserRole) =>
      createCalculationMutation.mutateAsync({ calculation, role }),
  };
};
